/**
 * client/src/features/income/services/incomeService.js
 * 
 * Service for income-related API interactions.
 * Handles CRUD operations and special operations like pay stub uploads.
 */
import axiosInstance from '../../../utils/axiosConfig';
import { logApiCall, logApiError } from './api';

// Base API URL
const API_URL = '/api/income';

/**
 * Get all income entries
 * @param {Object} options - Options including partnerAccess and includePartner flags
 * @returns {Promise} Promise with income data
 */
const getAll = async (options = {}) => {
  try {
    console.log('🔄 Fetching all income entries...', options);
    
    let response;
    if (options.partnerAccess) {
      // Use cashflow endpoint for partner access with partner=true query param
      console.log('🤝 Using cashflow endpoint for partner access');
      response = await axiosInstance.get('/api/cashflow/income?partner=true');
    } else {
      // Use regular endpoint for user's own data
      const url = options.includePartner ? `${API_URL}?includePartner=true` : API_URL;
      response = await axiosInstance.get(url);
    }
    
    // Log the response for debugging
    console.log('🔍 All incomes response:', response);
    
    // Handle different response formats that might come from the API
    let incomes = [];
    if (response.data && Array.isArray(response.data)) {
      incomes = response.data;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      incomes = response.data.data;
    } else if (response.data && response.data.incomes && Array.isArray(response.data.incomes)) {
      incomes = response.data.incomes;
    }
    
    console.log(`✅ Successfully retrieved ${incomes.length} income entries`);
    logApiCall('GET', options.partnerAccess ? '/api/cashflow/income' : API_URL, null, incomes);
    
    // Return the data in a consistent format
    return {
      data: incomes
    };
  } catch (err) {
    console.error('❌ Error fetching all income entries:', err);
    logApiError('GET', options.partnerAccess ? '/api/cashflow/income' : API_URL, null, err);
    throw err;
  }
};

/**
 * Get a specific income entry by ID
 * @param {string} id - Income entry ID
 * @returns {Promise} Promise with income data
 */
const getById = async (id) => {
  try {
    console.log(`🔄 Getting income entry ${id}...`);
    const response = await axiosInstance.get(`${API_URL}/${id}`);
    logApiCall('GET', `${API_URL}/${id}`, { id }, response.data);
    return response;
  } catch (err) {
    logApiError('GET', `${API_URL}/${id}`, { id }, err);
    throw err;
  }
};

/**
 * Create a new income entry
 * @param {Object} incomeData - New income data
 * @returns {Promise} Promise with created income data
 */
const create = async (incomeData) => {
  try {
    console.log('🔄 Creating new income entry:', incomeData);
    const response = await axiosInstance.post(API_URL, incomeData);
    logApiCall('POST', API_URL, incomeData, response.data);
    return response;
  } catch (err) {
    logApiError('POST', API_URL, incomeData, err);
    throw err;
  }
};

/**
 * Update an existing income entry
 * @param {string} id - Income entry ID
 * @param {Object} incomeData - Updated income data
 * @returns {Promise} Promise with updated income data
 */
const update = async (id, incomeData) => {
  try {
    console.log(`🔄 Updating income entry ${id}:`, incomeData);
    const response = await axiosInstance.put(`${API_URL}/${id}`, incomeData);
    logApiCall('PUT', `${API_URL}/${id}`, incomeData, response.data);
    return response;
  } catch (err) {
    logApiError('PUT', `${API_URL}/${id}`, incomeData, err);
    throw err;
  }
};

/**
 * Switch jobs for an income: creates a new income and deactivates the old one
 * @param {string} id - Existing income ID
 * @param {Object} data - { startDate, employer, basePerPeriod, frequency, signingBonus, rsu, vestCliffMonths, vestMonths, estSharePrice }
 */
const switchJob = async (id, data) => {
  try {
    console.log(`🔄 Switching job for income ${id}:`, data);
    const response = await axiosInstance.post(`${API_URL}/${id}/switch-job`, data);
    logApiCall('POST', `${API_URL}/${id}/switch-job`, data, response.data);
    return response;
  } catch (err) {
    logApiError('POST', `${API_URL}/${id}/switch-job`, data, err);
    throw err;
  }
};

/**
 * Estimate net pay preview (no persistence)
 */
const estimateNet = async (payload) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/estimate-net`, payload);
    logApiCall('POST', `${API_URL}/estimate-net`, payload, response.data);
    return response?.data;
  } catch (err) {
    logApiError('POST', `${API_URL}/estimate-net`, payload, err);
    throw err;
  }
};

/**
 * ZIP → location lookup (US-only)
 */
const lookupZip = async (zip) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/zip/${zip}`);
    logApiCall('GET', `${API_URL}/zip/${zip}`, null, response.data);
    return response?.data;
  } catch (err) {
    logApiError('GET', `${API_URL}/zip/${zip}`, null, err);
    throw err;
  }
};

/**
 * Analyze offer letter to prefill job switch form
 */
const analyzeOfferLetter = async (formData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/analyze-offer`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    });
    logApiCall('POST', `${API_URL}/analyze-offer`, {}, response.data);
    return response;
  } catch (err) {
    logApiError('POST', `${API_URL}/analyze-offer`, {}, err);
    throw err;
  }
};

/**
 * Delete an income entry
 * @param {string} id - Income entry ID
 * @returns {Promise} Promise with deletion confirmation
 */
const remove = async (id) => {
  try {
    console.log(`🔄 Deleting income entry ${id}`);
    const response = await axiosInstance.delete(`${API_URL}/${id}`);
    logApiCall('DELETE', `${API_URL}/${id}`, { id }, response.data);
    return response;
  } catch (err) {
    logApiError('DELETE', `${API_URL}/${id}`, { id }, err);
    throw err;
  }
};

/**
 * Upload a pay stub for an income entry
 * @param {string} incomeId - Income entry ID
 * @param {FormData} formData - Form data with pay stub file
 * @returns {Promise} Promise with updated income data
 */
const uploadPayStub = async (incomeId, formData) => {
  try {
    console.log(`🔄 Uploading pay stub for income ${incomeId}`);
    const response = await axiosInstance.post(`${API_URL}/${incomeId}/paystub`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    logApiCall('POST', `${API_URL}/${incomeId}/paystub`, { incomeId }, response.data);
    return response;
  } catch (err) {
    logApiError('POST', `${API_URL}/${incomeId}/paystub`, { incomeId }, err);
    throw err;
  }
};

/**
 * Get income entries by bucket
 * @param {string} bucketId - Bucket ID
 * @param {Object} options - Options including partnerAccess flag
 * @returns {Promise} Promise with income entries for the bucket
 */
const getByBucket = async (bucketId, options = {}) => {
  try {
    console.log(`🔄 Getting income entries for bucket ${bucketId}`);
    
    // First try to get all incomes with partner access options
    const allIncomesResponse = await getAll(options);
    const allIncomes = allIncomesResponse.data;
    
    // Filter incomes to find ones with matching bucketId or bucket field
    const bucketIncomes = Array.isArray(allIncomes) 
      ? allIncomes.filter(income => 
          (income.bucketId === bucketId) || 
          (income.bucket === bucketId) ||
          (income.bucket && income.bucket._id === bucketId)
        )
      : [];
    
    console.log(`🔍 Found ${bucketIncomes.length} incomes for bucket ${bucketId} from all incomes`);
    
    // Return the filtered incomes in the same format as the API would
    return {
      data: bucketIncomes
    };
  } catch (err) {
    console.error(`❌ Error retrieving incomes for bucket ${bucketId}:`, err);
    logApiError('GET', `/income for bucket ${bucketId}`, { bucketId }, err);
    throw err;
  }
};

/**
 * Process a pay stub document and extract income information
 * @param {FormData} formData - Form data with pay stub file
 * @returns {Promise} Promise with extracted income data
 */
const processPayStub = async (formData) => {
  try {
    console.log('🔄 Processing pay stub document');
    const response = await axiosInstance.post(`${API_URL}/process-paystub`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    logApiCall('POST', `${API_URL}/process-paystub`, null, response.data);
    return response;
  } catch (err) {
    logApiError('POST', `${API_URL}/process-paystub`, null, err);
    throw err;
  }
};

/**
 * Get income statistics
 * @returns {Promise} Promise with income statistics
 */
const getStatistics = async () => {
  try {
    console.log('🔄 Fetching income statistics');
    const response = await axiosInstance.get(`${API_URL}/statistics`);
    logApiCall('GET', `${API_URL}/statistics`, null, response.data);
    return response;
  } catch (err) {
    logApiError('GET', `${API_URL}/statistics`, null, err);
    throw err;
  }
};

/**
 * Get income projections
 * @param {Object} params - Projection parameters
 * @returns {Promise} Promise with income projections
 */
const getProjections = async (params) => {
  try {
    console.log('🔄 Fetching income projections');
    const response = await axiosInstance.get(`${API_URL}/projections`, { params });
    logApiCall('GET', `${API_URL}/projections`, params, response.data);
    return response;
  } catch (err) {
    logApiError('GET', `${API_URL}/projections`, params, err);
    throw err;
  }
};

/**
 * Reorder incomes within a bucket
 * @param {string} bucketId - Bucket ID
 * @param {Array} orderData - Array of objects with income ID and new order
 * @returns {Promise} Promise with reordering confirmation
 */
const reorderIncomes = async (bucketId, orderData) => {
  try {
    // Log the orderData array to verify it has the correct structure
    console.log(`🔄 Reordering incomes in bucket ${bucketId}:`, orderData);
    
    // Verify that each income has an _id
    const hasUndefinedIds = orderData.some(inc => !inc._id);
    if (hasUndefinedIds) {
      console.error('❌ Error: Some incomes have undefined _id:', orderData);
      throw new Error('Some incomes have undefined _id');
    }
    
    const response = await axiosInstance.put(`${API_URL}/reorder`, { bucketId, incomes: orderData });
    logApiCall('PUT', `${API_URL}/reorder`, { bucketId, orderData }, response.data);
    return response;
  } catch (err) {
    logApiError('PUT', `${API_URL}/reorder`, { bucketId, orderData }, err);
    throw err;
  }
};

/**
 * Move income to a different bucket
 * @param {string} incomeId - Income ID
 * @param {string} bucketId - Target bucket ID
 * @returns {Promise} Promise with updated income data
 */
const moveToBucket = async (incomeId, bucketId) => {
  try {
    console.log(`🔄 Moving income ${incomeId} to bucket ${bucketId}`);
    const response = await axiosInstance.put(`${API_URL}/${incomeId}/move-to-bucket`, { bucketId });
    logApiCall('PUT', `${API_URL}/${incomeId}/move-to-bucket`, { bucketId }, response.data);
    return response;
  } catch (err) {
    console.error(`❌ Error moving income to bucket:`, err);
    logApiError('PUT', `${API_URL}/${incomeId}/move-to-bucket`, { bucketId }, err);
    throw err;
  }
};

const incomeService = {
  getAll,
  getById,
  create,
  update,
  switchJob,
  analyzeOfferLetter,
  remove,
  uploadPayStub,
  getByBucket,
  processPayStub,
  getStatistics,
  getProjections,
  reorderIncomes,
  estimateNet,
  lookupZip,
  moveToBucket
};

export default incomeService; 